import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import { processTranslatableText } from '@/page-components/utils/functions';

type HtmlContentProps = {
  children?: React.ReactNode;
  styleText: string;
  className: string;
  properties?: {
    html?: string;
    allowRerender?: boolean;
  };
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const HtmlContent = (componentProps: HtmlContentProps) => {
  const { i18n, t } = useTranslation();
  const parentContext = React.useContext(DataElementContext);
  const [props, isVisible] = processComponentProps(componentProps, parentContext);
  const { properties, className, styleText } = props as HtmlContentProps;
  const { html: rawHtml = '', allowRerender = false } = properties ?? {};

  const html = React.useMemo(
    () => processTranslatableText(rawHtml, undefined, i18n.language, t),
    [rawHtml, i18n.language, t],
  );

  const divRef = React.useRef<HTMLDivElement>(null);
  const isFirstRender = React.useRef(true);

  React.useEffect(() => {
    if (!html || !divRef.current) return;
    if (!isFirstRender.current && !allowRerender) return;
    isFirstRender.current = false;

    const fragment = document.createRange().createContextualFragment(html);
    divRef.current.innerHTML = '';
    divRef.current.appendChild(fragment);
  }, [html, allowRerender]);

  if (!isVisible) {
    return null;
  }

  return (
    <DataElementContext.Provider value={parentContext}>
      <ModuleElementDiv ref={divRef} className={className ?? ''} $styleText={styleText} />
    </DataElementContext.Provider>
  );
};

export default HtmlContent;
